import { cva } from "class-variance-authority";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import type { RegrelloShape } from "../../tokens/enums/regrelloShape";
import type { RegrelloSize } from "../../tokens/enums/regrelloSize";
import type { RegrelloButtonVariant } from "./RegrelloButtonVariant";

export const buttonVariants = cva<{
  active: Record<"true" | "false", string>;
  iconOnly: Record<RegrelloSize, string>;
  intent: Record<Exclude<RegrelloIntent, "warning" | "success">, string>;
  pressed: Record<"true" | "false", string>;
  shape: Record<RegrelloShape, string>;
  size: Record<RegrelloSize, string>;
  variant: Record<RegrelloButtonVariant, string>;
}>(
  [
    // Native <button> tags support the `disabled` attribute, so these classes will work for those:
    "disabled:opacity-30",
    "disabled:cursor-not-allowed",

    // Native <a> tags don't support the `disabled` attribute, so we use `data-disabled` instead:
    "aria-[disabled=true]:opacity-30",
    "aria-[disabled=true]:cursor-not-allowed",

    "focus-visible:outline-2",
    "focus-visible:outline-primary-solid",
    "focus-visible:outline-offset-3",

    "font-medium",
    "inline-flex",
    "items-center",
    "justify-center",
    "text-sm",
    "relative", // To ensure we contain the 'loading' spinner.

    "border",
    "border-transparent",
  ],
  {
    variants: {
      /**
       * Whether this button is in an active or selected state.
       *
       * @default false
       */
      active: {
        true: "",
        false: "",
      },

      /**
       * The shape to use for the button's corners.
       *
       * @default RegrelloShape.RECTANGLE
       */
      shape: {
        rectangle: "rounded",
        circle: "rounded-full",
      },

      /**
       * The semantic intent the button should convey.
       *
       * @default RegrelloIntent.PRIMARY
       */
      intent: {
        neutral: "",
        primary: "",
        special: "",
        danger: "",
      },

      pressed: {
        true: "",
        false: "",
      },

      /**
       * The visual weight with which the button should appear.
       *
       * @default RegrelloButtonVariant.SOLID
       */
      variant: {
        solid: "",
        soft: "",
        outline: "",
        ghost: "",
        surface: "",
      },

      /**
       * The size of the button.
       *
       * @default RegrelloSize.MEDIUM
       */
      size: {
        "x-small": "px-1.5 py-0.75 text-xs",
        small: "px-2 py-0.75",
        medium: "px-2.5 py-1.25",
        large: "px-3 py-1.75",
        "x-large": "px-3.5 py-1.75 text-base",
      },
      iconOnly: {
        "x-small": "w-6 h-6",
        small: "w-7 h-7",
        medium: "w-8 h-8",
        large: "w-9 h-9",
        "x-large": "w-10 h-10",
      },
    },
    compoundVariants: [
      // Solid
      {
        intent: "primary",
        variant: "solid",
        class: [
          "bg-primary-solid",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-solidHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:rg-solidPressed",
          "text-textContrast",
        ],
      },
      {
        intent: "special",
        variant: "solid",
        class: [
          "bg-special-solid",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-solidHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:rg-solidPressed",
          "text-textContrast",
        ],
      },
      {
        intent: "danger",
        variant: "solid",
        class: [
          "bg-danger-solid",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-solidHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:rg-solidPressed",
          "text-textContrast",
        ],
      },
      {
        intent: "neutral",
        variant: "solid",
        class: [
          "bg-neutral-solid",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-solidHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:rg-solidPressed",
          "text-textContrast",
        ],
      },

      // Soft
      {
        intent: "primary",
        variant: "soft",
        class: [
          "bg-primary-soft",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-softHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-softPressed",
          "text-primary-textMuted",
        ],
      },
      {
        intent: "special",
        variant: "soft",
        class: [
          "bg-special-soft",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-softHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-softPressed",
          "text-special-textMuted",
        ],
      },
      {
        intent: "danger",
        variant: "soft",
        class: [
          "bg-danger-soft",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-softHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-softPressed",
          "text-danger-textMuted",
        ],
      },
      {
        intent: "neutral",
        variant: "soft",
        class: [
          "bg-neutral-soft",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-softHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-softPressed",
          "text-neutral-textMuted",
        ],
      },

      // Outline
      {
        intent: "primary",
        variant: "outline",
        class: [
          "bg-primary-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-transparentPressed",
          "border-primary-borderInteractiveStrong",
          "text-primary-textMuted",
        ],
      },
      {
        intent: "primary",
        variant: "outline",
        active: true,
        class: [
          "bg-primary-transparentPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-border",
        ],
      },
      {
        intent: "special",
        variant: "outline",
        class: [
          "bg-special-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-transparentPressed",
          "box-inset",
          "shadow-special-borderInteractiveStrong",
          "text-special-textMuted",
        ],
      },
      {
        intent: "special",
        variant: "outline",
        active: true,
        class: [
          "bg-special-transparentPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-border",
        ],
      },
      {
        intent: "danger",
        variant: "outline",
        class: [
          "bg-danger-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-transparentPressed",
          "border-danger-borderInteractiveStrong",
          "text-danger-textMuted",
        ],
      },
      {
        intent: "danger",
        variant: "outline",
        active: true,
        class: [
          "bg-danger-transparentPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-border",
        ],
      },
      {
        intent: "neutral",
        variant: "outline",
        class: [
          "bg-neutral-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-transparentPressed",
          "border-neutral-borderInteractiveStrong",
          "text-textMuted",
        ],
      },
      {
        intent: "neutral",
        variant: "outline",
        active: true,
        class: [
          "bg-neutral-transparentPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-border",
        ],
      },

      // Ghost
      {
        intent: "primary",
        variant: "ghost",
        class: [
          "bg-primary-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-transparentPressed",
          "text-primary-textMuted",
        ],
      },
      {
        intent: "primary",
        variant: "ghost",
        active: true,
        class: [
          "bg-primary-softHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-border",
        ],
      },
      {
        intent: "special",
        variant: "ghost",
        class: [
          "bg-special-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-transparentPressed",
          "text-special-textMuted",
        ],
      },
      {
        intent: "special",
        variant: "ghost",
        active: true,
        class: [
          "bg-special-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-border",
        ],
      },
      {
        intent: "danger",
        variant: "ghost",
        class: [
          "bg-danger-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-transparentPressed",
          "text-danger-textMuted",
        ],
      },
      {
        intent: "danger",
        variant: "ghost",
        active: true,
        class: [
          "bg-danger-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-border",
        ],
      },
      {
        intent: "neutral",
        variant: "ghost",
        class: [
          "bg-neutral-transparent",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-transparentHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-transparentPressed",
          "text-textMuted",
        ],
      },
      {
        intent: "neutral",
        variant: "ghost",
        active: true,
        class: [
          "bg-neutral-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-softPressed",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-border",
        ],
      },

      // Surface
      {
        intent: "primary",
        variant: "surface",
        class: [
          "bg-primary-surface",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-primary-surfaceHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-primary-surfacePressed",
          "border",
          "border-primary-borderInteractive",
          "text-primary-textMuted",
        ],
      },
      {
        intent: "special",
        variant: "surface",
        class: [
          "bg-special-surface",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-special-surfaceHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-special-surfacePressed",
          "border",
          "border-special-borderInteractive",
          "text-special-textMuted",
        ],
      },
      {
        intent: "danger",
        variant: "surface",
        class: [
          "bg-danger-surface",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-danger-surfaceHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-danger-surfacePressed",
          "border",
          "border-danger-borderInteractive",
          "text-danger-textMuted",
        ],
      },
      {
        intent: "neutral",
        variant: "surface",
        class: [
          "bg-neutral-surface",
          "[&:not(:disabled):not([aria-disabled=true])]:hover:bg-neutral-surfaceHovered",
          "[&:not(:disabled):not([aria-disabled=true])]:active:bg-neutral-surfacePressed",
          "border",
          "border-neutral-borderInteractive",
          "text-textMuted",
        ],
      },
    ],
    defaultVariants: {
      intent: "neutral",
      shape: "rectangle",
      variant: "solid",
    },
  },
);

/**
 * Button variants strictly for the "pressed" state, intended specifically for buttons backed by <a>
 * tags that need to respond to "keyboard clicks" with Space or Enter.
 *
 * This should be spread to clsx() as overrides after `buttonVariants`:
 *
 * @example
 * ```tsx
 * clsx(buttonVariants(...), buttonPressedVariants({ isPressed, ... }))
 * ```
 *
 * ___Note:___ This is separate from `buttonVariants()` because there are some very subtle bugs that
 * can happen with calling code when attempting to override an initial `buttonVariants()` result
 * with (hypothetically) `buttonVariants({ pressed: true })`. It's much easier and more bug-free to
 * do `clsx(buttonVariants(...), buttonPressedVariants(...))` instead.
 */
export const buttonPressedVariants = cva<{
  intent: Record<Exclude<RegrelloIntent, "warning" | "success">, string>;
  pressed: Record<"true" | "false", string>;
  variant: Record<RegrelloButtonVariant, string>;
}>([], {
  variants: {
    /**
     * The semantic intent the button should convey.
     *
     * @default RegrelloIntent.PRIMARY
     */
    intent: {
      neutral: "",
      primary: "",
      special: "",
      danger: "",
    },

    /**
     * The visual weight with which the button should appear.
     *
     * @default RegrelloButtonVariant.SOLID
     */
    variant: {
      solid: "",
      soft: "",
      outline: "",
      ghost: "",
      surface: "",
    },

    pressed: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    // Solid
    {
      intent: "primary",
      variant: "solid",
      pressed: true,
      class: ["bg-primary-solidHovered", "rg-solidPressed"],
    },
    {
      intent: "special",
      variant: "solid",
      pressed: true,
      class: ["bg-special-solidHovered", "rg-solidPressed"],
    },
    {
      intent: "danger",
      variant: "solid",
      pressed: true,
      class: ["bg-danger-solidHovered", "rg-solidPressed"],
    },
    {
      intent: "neutral",
      variant: "solid",
      pressed: true,
      class: ["bg-neutral-solidHovered", "rg-solidPressed"],
    },

    // Soft
    {
      intent: "primary",
      variant: "soft",
      pressed: true,
      class: ["bg-primary-softPressed"],
    },
    {
      intent: "special",
      variant: "soft",
      pressed: true,
      class: ["bg-special-softPressed"],
    },
    {
      intent: "danger",
      variant: "soft",
      pressed: true,
      class: ["bg-danger-softPressed"],
    },
    {
      intent: "neutral",
      variant: "soft",
      pressed: true,
      class: ["bg-neutral-softPressed"],
    },

    // Outline
    {
      intent: "primary",
      variant: "outline",
      pressed: true,
      class: ["bg-primary-transparentPressed"],
    },

    {
      intent: "special",
      variant: "outline",
      pressed: true,
      class: ["bg-special-transparentPressed"],
    },
    {
      intent: "danger",
      variant: "outline",
      pressed: true,
      class: ["bg-danger-transparentPressed"],
    },
    {
      intent: "neutral",
      variant: "outline",
      pressed: true,
      class: ["bg-neutral-transparentPressed"],
    },
    {
      intent: "neutral",
      variant: "outline",
      pressed: true,
      class: ["bg-neutral-transparentPressed"],
    },

    // Ghost
    {
      intent: "primary",
      variant: "ghost",
      pressed: true,
      class: ["bg-primary-transparentPressed"],
    },
    {
      intent: "special",
      variant: "ghost",
      pressed: true,
      class: ["bg-special-transparentPressed"],
    },
    {
      intent: "danger",
      variant: "ghost",
      pressed: true,
      class: ["bg-danger-transparentPressed"],
    },
    {
      intent: "neutral",
      variant: "ghost",
      pressed: true,
      class: ["bg-neutral-transparentPressed"],
    },

    // Surface
    {
      intent: "primary",
      variant: "surface",
      pressed: true,
      class: ["bg-primary-surfacePressed"],
    },
    {
      intent: "danger",
      variant: "surface",
      pressed: true,
      class: ["bg-danger-surfacePressed"],
    },
    {
      intent: "neutral",
      variant: "surface",
      pressed: true,
      class: ["bg-neutral-surfacePressed"],
    },
  ],
  defaultVariants: {
    intent: "neutral",
    variant: "solid",
    pressed: false,
  },
});
